import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { IMaskInput } from 'react-imask';
import Adornment from './Adornment';
/**
 * This component is custom built for Asure Cloud. This input is restricted to numbers, it enables the user to interact with and input content and data.
 * It also includes the ability to add adornments to both before and after the input element.
 * Pass in custom HTML or ReactNode elements in either `startAdornmentElement` or `endAdornmentElement` to render custom elements used inside of the adornments.
 * For best results use [Font Awesome](https://fontawesome.com/) icons for the adornments.
 * For additional props, refer to [Input element on MDN Web Docs](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input) for input attributes that can be used with this component.
 * To import the component use the import statement: `import { AsureCloudTextInput } from '@asuresoftware/asure.design-system'`
 * 
 * Masked properties:
 * 
 * scale: digits after point, 0 for integers
 * 
 * signed: true/false - allow/disallow negatives
 * 
 * thousandsSeparator: ',' by default
 * 
 * padFractionalZeros: t/f if true pads zeros at end to the length of scale
 * 
 * normalizeZeros: t/f appends or removes zeros at ends
 * 
 * radix: fractional delimiter
 * 
 * min: minimum amount
 * 
 * max: maximum amount
 */
export interface MaskedNumberInput extends React.InputHTMLAttributes<HTMLInputElement> {  
  labelDisplay: string;
  helperText?: string;  
  signed?: boolean;
  scale?: number;
  thousandsSeparator?: string;
  padFractionalZeros?: boolean;
  normalizeZeros?: boolean;
  radix?: string;
  min?: number;
  max?: number;
  onUpdate: any;
  id: string;
  inputClassName?: string;
  wrapperClassName?: string;
  endAdornmentElement?: React.ReactNode | string;
  startAdornmentElement?: React.ReactNode | string;
  ref?: React.MutableRefObject<HTMLInputElement | null>;
}

export const MaskedNumberInput = ({
  wrapperClassName,
  endAdornmentElement,
  helperText,
  inputClassName,
  labelDisplay,
  startAdornmentElement,
  signed,
  scale,
  thousandsSeparator,
  padFractionalZeros,
  normalizeZeros,
  radix,
  min,
  max,
  onUpdate,
  ...props}: MaskedNumberInput) => {

  const regexPattern = signed === true ? new RegExp(`^(-)?(([1-9](\\d*|\\d{0,2}(${thousandsSeparator ? thousandsSeparator : ','}\\d{3})*))|0)(\\${radix ? radix : '.'}\\d{1,${scale ? scale : 2}})?$`) : new RegExp(`^(([1-9](\\d*|\\d{0,2}(${thousandsSeparator ? thousandsSeparator : ','}\\d{3})*))|0)(\\${radix ? radix : '.'}\\d{1,${scale ? scale : 2}})?$`);
  const [isInvalid, setInvalid] = useState<boolean>(false);
  const [isComplete, setComplete] = useState<boolean>(false);
  const [isLazy, setLazy] = useState<boolean>(true);
  const [maskedRef, setMaskedRef] = useState<HTMLInputElement>();
  const [inputValue, setValue] = useState<string>('');
  const requiredClass = props.required ? ' required' : '';
  const disabledClass = props.disabled ? ' disabled' : '';
  const invalidClass = isInvalid ? ' invalid' : '';
  const wrapperClass = wrapperClassName ? ' ' + wrapperClassName : '';
  const inputClass = inputClassName ? ' ' + inputClassName : '';

  const onInputUpdate = (value, mask) => {
      setMaskedRef(mask.el.input);
      const submitValue = mask.value;
      onUpdate(submitValue);
      setValue(submitValue);
  };
  const onBlur = () => {
      setComplete(true);
      setLazy(true);
  };
  const resetValidation = () => {
      if(maskedRef !== undefined) { maskedRef.setCustomValidity('') };
      setInvalid(false);
      setComplete(false);
  };
  const validateField = () => {
    if((maskedRef !== undefined && (props.required || inputValue !== '')) && !inputValue.match(regexPattern)) {
      maskedRef.setCustomValidity(`Please match the format requested.`);
      setInvalid(true);
    }
  };

    useEffect(() => validateField(), [isComplete]);
    useEffect(() => resetValidation(), [inputValue]);
  return (
    <TextInputWrapper className={`input-wrapper mb-1${wrapperClass}`}>
      <label
        htmlFor={props.id}
        className={`d-block${invalidClass}${requiredClass}${disabledClass}`}
      >
        {labelDisplay}
      </label>
      <div className="input-container d-flex">
        <IMaskInput 
          {...props}
          pattern={regexPattern.source}
          mask={Number}
          className={`order-1 p-2${inputClass}`}
          data-lpignore="true"
          maxLength={props.maxLength || 255}
          minLength={props.minLength || 0 }
          lazy={isLazy}
          onAccept={(value, mask) => onInputUpdate(value, mask)}
          onBlur={() => onBlur()}
          onInvalidCapture={() => setInvalid(true)}
          onInput={() => {setLazy(false); isInvalid && setInvalid(false)}}
          onChange={(e) => e.preventDefault()}
          signed={signed}
          scale={scale}
          thousandsSeparator={thousandsSeparator}
          padFractionalZeros={padFractionalZeros}
          normalizeZeros={normalizeZeros}
          radix={radix}
          max={max}
          min={min}
        />
        {startAdornmentElement && (
          <Adornment
            className="order-0"
            customElement={startAdornmentElement}
            position="prefix"
          />
        )}
        {endAdornmentElement && (
          <Adornment
            className="order-2"
            customElement={endAdornmentElement}
            position="suffix"
          />
        )}
      </div>
      <small>{helperText}</small>
    </TextInputWrapper>
    
  )
}
export default MaskedNumberInput;

MaskedNumberInput.defaultProps = {
  scale: 0,
  radix: '.',
  thousandsSeparator: ','
}

const TextInputWrapper = styled.div`
  label {
    color: #434343;
    font-size: 0.875rem;
  }

  .required:after {
    content: '*';
    margin-left: 0.15rem;
    display: inline-block;
    color: #a20011;
  }

  .input-container {
    input {
      font-size: 0.875rem;
      border: 1px solid #ccc;
      color: black;
      outline: none;
      z-index: 1;

      &::placeholder {
        color: #bbbbbb;
      }

      &:focus {
        border-color: #006c93;
        box-shadow: 0px 0px 4px 2px rgba(0, 108, 147, 0.25);
      }

      &:disabled {
        color: #333333;
        -webkit-text-fill-color: #333333;
        background-color: #eee;
      }
      
      &:read-only {
        color: #333333;
        background-color: #eee;
      }
    }
  }

  .invalid ~ .input-container input {
    border-color: #a20011;
  }

  .invalid ~ .input-container input:focus {
    box-shadow: 0px 0px 4px 2px rgba(162, 0, 17, 0.25);
  }

  .invalid ~ .input-container span {
    color: #a20011;
    border-color: #a20011;
    background-color: #efdfde;
  }

  .invalid ~ .input-container svg {
    fill: #a20011;
  }

  small {
    color: #757575;
  }

  .invalid ~ small {
    color: #a20011;
  }
`;
